import React, { useRef, forwardRef, useEffect, useImperativeHandle } from 'react';
import { FriendlyCaptchaSDK } from '@friendlycaptcha/sdk';

let sdkInstance: InstanceType<typeof FriendlyCaptchaSDK> | null = null;
const getSDK = () => {
    if (!sdkInstance) {
        // Check if mock is already injected (by E2E tests or other tools)
        if (typeof window !== 'undefined' && (window as any).FriendlyCaptchaSDK) {
            sdkInstance = new (window as any).FriendlyCaptchaSDK({ apiEndpoint: 'global' });
        } else {
            sdkInstance = new FriendlyCaptchaSDK({ apiEndpoint: 'global' } as any);
        }
    }
    return sdkInstance!;
};

const SITE_KEY = process.env.REACT_APP_FRIENDLY_CAPTCHA_SITEKEY;

export type FriendlyCaptchaHandle = {
    reset: () => void;
};

type FriendlyCaptchaWidgetProps = {
    onComplete?: (response: string) => void;
    onError?: (error: unknown) => void;
    onExpire?: () => void;
    lang?: string;
    startMode?: 'none' | 'focus' | 'auto';
    triggerRef?: React.RefObject<HTMLElement>;
};

export const FriendlyCaptchaWidget = forwardRef<FriendlyCaptchaHandle, FriendlyCaptchaWidgetProps>(
    (
        {
            onComplete,
            onError,
            onExpire,
            lang = 'fr',
            startMode = 'none',
            triggerRef,
        },
        ref
    ) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const widgetRef = useRef<any>(null);

        useEffect(() => {
            // Defer creation so React StrictMode's synchronous cleanup can cancel the
            // timer before any iframe is created, avoiding "destroy-before-load" retries.
            const timerId = setTimeout(() => {
                if (!containerRef.current) return;
                const sdk = getSDK();
                widgetRef.current = sdk.createWidget({
                    element: containerRef.current,
                    sitekey: SITE_KEY!,
                    startMode,
                    language: lang,
                });
            }, 0);

            return () => {
                clearTimeout(timerId);
                // sdk.clear() destroys all widgets AND removes the agent iframe from
                // the DOM, resetting the SDK's internal agent map. Using widget.destroy()
                // alone leaves the agent in a stale state that causes "Duplicate widget
                // announce" on the next createWidget call.
                sdkInstance?.clear();
                widgetRef.current = null;
            };
        }, []); // eslint-disable-line react-hooks/exhaustive-deps

        useEffect(() => {
            if (!triggerRef?.current || startMode !== 'none') return;

            const handleFocus = () => widgetRef.current?.start();
            const el = triggerRef.current;
            el.addEventListener('focus', handleFocus);

            // eslint-disable-next-line consistent-return
            return () => el.removeEventListener('focus', handleFocus);
        }, [triggerRef]); // eslint-disable-line react-hooks/exhaustive-deps

        useEffect(() => {
            const element = containerRef.current;
            if (!element) return;

            const handleComplete = (e: Event) => onComplete && onComplete((e as CustomEvent).detail.response);
            const handleError = (e: Event) => onError && onError((e as CustomEvent).detail.error);
            const handleExpire = () => onExpire && onExpire();

            element.addEventListener('frc:widget.complete', handleComplete);
            element.addEventListener('frc:widget.error', handleError);
            element.addEventListener('frc:widget.expire', handleExpire);

            return () => {
                element.removeEventListener('frc:widget.complete', handleComplete);
                element.removeEventListener('frc:widget.error', handleError);
                element.removeEventListener('frc:widget.expire', handleExpire);
            };
        }, [onComplete, onError, onExpire]);

        useImperativeHandle(ref, () => ({
            reset: () => widgetRef.current?.reset(),
        }));

        return <div ref={containerRef} />;
    }
);

FriendlyCaptchaWidget.displayName = 'FriendlyCaptchaWidget';
